import { Transition } from "@headlessui/react"
import { Form, Head, router, useForm, usePage } from "@inertiajs/react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/app-layout"
import {
  editTeamPath,
  leaveTeamPath,
  teamMembershipPath,
  teamPath,
  teamTeamInvitationPath,
  teamTeamInvitationsPath,
} from "@/routes"
import type {
  BreadcrumbItem,
  Membership,
  SharedProps,
  TeamInvitation,
} from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Team Settings", href: editTeamPath() },
]

interface Props {
  members: Membership[]
  pending_invitations: TeamInvitation[]
}

export default function TeamsEdit({ members, pending_invitations }: Props) {
  const { auth } = usePage<SharedProps>().props
  const ownerId = auth.current_team?.owner_id
  const isOwner = ownerId === auth.user.id

  const addMemberForm = useForm({ email: "" })

  function handleAddMember(e: React.FormEvent) {
    e.preventDefault()
    addMemberForm.post(teamTeamInvitationsPath(), {
      preserveScroll: true,
      onSuccess: () => addMemberForm.reset("email"),
    })
  }

  function handleRemoveMember(membershipId: number) {
    if (!confirm("Are you sure you want to remove this member?")) return

    router.delete(teamMembershipPath(membershipId), {
      preserveScroll: true,
    })
  }

  function handleCancelInvitation(invitationId: number) {
    if (!confirm("Are you sure you want to cancel this invitation?")) return

    router.delete(teamTeamInvitationPath(invitationId), {
      preserveScroll: true,
    })
  }

  function handleLeaveTeam() {
    if (
      !confirm(
        `Are you sure you want to leave ${auth.current_team?.name ?? "this team"}?`,
      )
    )
      return

    router.post(leaveTeamPath())
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Team Settings" />

      <div className="space-y-6 px-4 py-6">
        <HeadingSmall title="Team Settings" description="Manage your team" />

        {isOwner && (
          <>
            <Form
              method="patch"
              action={teamPath()}
              options={{ preserveScroll: true }}
              className="space-y-6"
            >
              {({ errors, processing, recentlySuccessful }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>

                    <Input
                      id="name"
                      name="name"
                      className="mt-1 block w-full"
                      defaultValue={auth.current_team?.name ?? ""}
                      required
                      placeholder="Team name"
                    />

                    <InputError className="mt-2" messages={errors.name} />
                  </div>

                  <div className="flex items-center gap-4">
                    <Button disabled={processing}>Save</Button>

                    <Transition
                      show={recentlySuccessful}
                      enter="transition ease-in-out"
                      enterFrom="opacity-0"
                      leave="transition ease-in-out"
                      leaveTo="opacity-0"
                    >
                      <p className="text-sm text-neutral-600">Saved</p>
                    </Transition>
                  </div>
                </>
              )}
            </Form>

            <Separator />
          </>
        )}

        <HeadingSmall
          title="Team Members"
          description={
            isOwner
              ? "Add or remove members from your team"
              : "Members of your team"
          }
        />

        <div className="space-y-3">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{member.name}</p>
                    {member.user_id === ownerId && (
                      <Badge variant="secondary">Owner</Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {member.email}
                  </p>
                </div>
              </div>

              {isOwner && member.user_id !== ownerId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => handleRemoveMember(member.id)}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
        </div>

        {isOwner && (
          <>
            {pending_invitations.length > 0 && (
              <div className="space-y-3">
                {pending_invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">
                            {invitation.email}
                          </p>
                          <Badge
                            variant={
                              invitation.status === "declined"
                                ? "destructive"
                                : "outline"
                            }
                          >
                            {invitation.status === "declined"
                              ? "Declined"
                              : "Pending"}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {invitation.status === "pending" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelInvitation(invitation.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleAddMember} className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Invite member by email</Label>
                <div className="flex gap-2">
                  <Input
                    id="email"
                    type="email"
                    value={addMemberForm.data.email}
                    onChange={(e) =>
                      addMemberForm.setData("email", e.target.value)
                    }
                    placeholder="colleague@example.com"
                    className="max-w-sm"
                    required
                  />
                  <Button type="submit" disabled={addMemberForm.processing}>
                    Invite
                  </Button>
                </div>
                <InputError messages={addMemberForm.errors.email} />
              </div>
            </form>
          </>
        )}

        {!isOwner && (
          <>
            <Separator />

            <div className="space-y-4">
              <HeadingSmall
                title="Leave Team"
                description="Remove yourself from this team"
              />

              <Button
                variant="outline"
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={handleLeaveTeam}
              >
                Leave Team
              </Button>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}
